import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { storageService } from '../services/storageService';
import { StorageFile, UploadProgress, StorageUsage } from '../types/storage';

interface ContentManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFiles?: (files: StorageFile[]) => void;
  selectionMode?: 'single' | 'multiple';
  initialTab?: 'image' | 'template' | 'file' | 'graphic' | 'media';
}

const ContentManagementModal: React.FC<ContentManagementModalProps> = ({
  isOpen,
  onClose,
  onSelectFiles,
  selectionMode = 'multiple',
  initialTab = 'image',
}) => {
  const { currentUser } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState<'image' | 'template' | 'file' | 'graphic' | 'media'>(initialTab);
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Load files and storage usage when modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadFiles();
      loadStorageUsage();
    }
}, [isOpen, currentUser, currentWorkspace, activeTab]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setSelectedFiles([]);
    }
  }, [isOpen, initialTab]);

  const loadFiles = async () => {
    if (!currentUser) return;
    
    try {
      const userFiles = await storageService.getFiles(
        currentUser.uid, 
        currentWorkspace?.id, 
        activeTab
      );
      setFiles(userFiles);
    } catch (error) {
      console.error('Error loading files:', error);
    }
  };

  const loadStorageUsage = async () => {
    if (!currentUser) return;
    
    try {
      const usage = await storageService.getStorageUsage(
        currentUser.uid, 
        currentWorkspace?.id
      );
      setStorageUsage(usage);
    } catch (error) {
      console.error('Error loading storage usage:', error);
    }
  };

  const tabs = [
    { id: 'image', name: 'Images', icon: '🖼️' },
    { id: 'template', name: 'Templates', icon: '📄' },
    { id: 'file', name: 'Files', icon: '📁' },
    { id: 'graphic', name: 'Graphics', icon: '🎨' },
    { id: 'media', name: 'Media Library', icon: '🎬' }
  ] as const;

  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles((prev) => {
      if (selectionMode === 'single') {
        return prev.includes(fileId) ? [] : [fileId];
      }
      if (prev.includes(fileId)) {
        return prev.filter((id) => id !== fileId);
      }
      return [...prev, fileId];
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0 || !currentUser) return;

    setIsUploading(true);
    const newUploadProgress: UploadProgress[] = [];

    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const progressId = `upload_${Date.now()}_${i}`;
        
        const initialProgress: UploadProgress = {
          fileId: progressId,
          fileName: file.name,
          progress: 0,
          status: 'uploading'
        };

        // Add to progress array immediately
        setUploadProgress(prev => [...prev, initialProgress]);

        try {
          console.log('Starting upload for file:', file.name, 'Size:', file.size);
          const uploadedFile = await storageService.uploadFile(
            file,
            currentUser.uid,
            currentWorkspace?.id,
            [],
            undefined,
            (progress) => {
              console.log('Upload progress:', progress);
              setUploadProgress(prev => 
                prev.map(p => p.fileId === progressId ? progress : p)
              );
            }
          );
          console.log('Upload completed:', uploadedFile);

          // Update progress to completed
          setUploadProgress(prev => 
            prev.map(p => p.fileId === progressId ? { ...p, status: 'completed', progress: 100 } : p)
          );

        } catch (error) {
          console.error('Upload error:', error);
          console.error('Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            code: (error as any)?.code,
            stack: error instanceof Error ? error.stack : undefined
          });
          setUploadProgress(prev => 
            prev.map(p => p.fileId === progressId ? { 
              ...p, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed' 
            } : p)
          );
        }
      }

      // Reload files and storage usage
      await loadFiles();
      await loadStorageUsage();

    } finally {
      setIsUploading(false);
      // Clear upload progress after 3 seconds
      setTimeout(() => {
        setUploadProgress([]);
      }, 3000);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!currentUser) return;
    
    try {
      await storageService.deleteFile(fileId, currentUser.uid, currentWorkspace?.id);
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (!currentUser || selectedFiles.length === 0) return;
    
    try {
      await storageService.bulkDeleteFiles(selectedFiles, currentUser.uid, currentWorkspace?.id);
      setSelectedFiles([]);
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error bulk deleting files:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStoragePercentage = (): number => {
    if (!storageUsage) return 0;
    return (storageUsage.totalSize / storageUsage.quota.totalStorage) * 100;
  };

  const renderContent = () => {
    const filteredFiles = files.filter(file => 
      searchTerm === '' || 
      file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const renderFileGrid = (files: StorageFile[]) => (
      <div className="space-y-4">
        {files.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No files found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by uploading your first file.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
            {files.map((file) => (
              <div key={file.id} className="relative group">
                <div
                  className={`aspect-[4/3] bg-slate-100 rounded-2xl border-2 transition-all duration-300 overflow-hidden cursor-pointer shadow-sm hover:shadow-xl hover:-translate-y-1 ${
                    selectedFiles.includes(file.id!) 
                      ? 'border-ocean-500 ring-4 ring-ocean-500/10' 
                      : 'border-transparent hover:border-ocean-200'
                  }`}
                  onClick={() => toggleFileSelection(file.id!)}
                >
                  {file.type === 'image' ? (
                    <img 
                      src={file.url} 
                      alt={file.originalName}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4">
                      <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">
                        {file.type === 'template' ? '📄' : 
                         file.type === 'file' ? '📁' : 
                         file.type === 'graphic' ? '🎨' : '🎬'}
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 text-center truncate w-full px-2">
                        {file.originalName}
                      </div>
                    </div>
                  )}
                  
                  {/* Selection Overlay */}
                  <div className={`absolute inset-0 bg-ocean-600/10 transition-opacity duration-300 ${selectedFiles.includes(file.id!) ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}></div>
                </div>
                
                {/* Checkbox Styled */}
                <div className="absolute top-3 left-3 z-10">
                  <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                    selectedFiles.includes(file.id!) 
                      ? 'bg-ocean-500 border-ocean-500 shadow-lg shadow-ocean-500/30' 
                      : 'bg-white/80 border-slate-200 backdrop-blur-sm'
                  }`}>
                    {selectedFiles.includes(file.id!) && (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Delete button */}
                <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFile(file.id!);
                    }}
                    className="p-1.5 bg-white/90 backdrop-blur-sm text-rose-500 rounded-lg shadow-sm border border-slate-100 hover:bg-rose-500 hover:text-white transition-all duration-200"
                    title="Delete item"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                {/* Name Label */}
                <div className="mt-3 px-1 text-center">
                  <p className="text-xs font-bold text-slate-700 truncate group-hover:text-ocean-600 transition-colors">{file.originalName}</p>
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-tight">{formatFileSize(file.size)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    const renderFileList = (files: StorageFile[]) => (
      <div className="space-y-2">
        {files.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No files found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by uploading your first file.</p>
          </div>
        ) : (
          files.map((file) => (
            <div
              key={file.id}
              className={`flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${
                selectedFiles.includes(file.id!) ? 'bg-blue-50 border border-blue-200' : ''
              }`}
              onClick={() => toggleFileSelection(file.id!)}
            >
              <input
                type="checkbox"
                checked={selectedFiles.includes(file.id!)}
                onChange={() => toggleFileSelection(file.id!)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
              />
              <span className="text-gray-500 mr-3 text-lg">
                {file.type === 'template' ? '📄' : 
                 file.type === 'file' ? '📁' : 
                 file.type === 'graphic' ? '🎨' : '🎬'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{file.originalName}</div>
                <div className="text-xs text-gray-500">{formatFileSize(file.size)} • {file.mimeType}</div>
              </div>
              <button
                onClick={() => handleDeleteFile(file.id!)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Delete file"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    );

    switch (activeTab) {
      case 'image':
      case 'graphic':
        return renderFileGrid(filteredFiles.filter(f => f.type === activeTab));
      case 'template':
      case 'media':
        return renderFileGrid(filteredFiles.filter(f => f.type === activeTab));
      case 'file':
        return renderFileList(filteredFiles.filter(f => f.type === 'file'));
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6 md:p-12">
      <div 
        ref={modalRef}
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-6xl h-[80vh] flex flex-col overflow-hidden border border-white/20 transition-all duration-500 animate-in fade-in zoom-in-95"
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 bg-white sticky top-0 z-10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Your Content</h2>
              <p className="text-sm text-slate-500 mt-1 font-medium italic">Manage and organize your visual brand assets</p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Modern Search */}
              <div className="relative group w-64">
                <input
                  type="text"
                  placeholder="Search your library..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-ocean-500/10 focus:border-ocean-400 transition-all duration-200 group-hover:bg-white"
                />
                <svg className="absolute left-3.5 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              <button
                onClick={onClose}
                className="p-2.5 bg-slate-50 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all duration-200 border border-slate-100"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Storage Usage Bar - Full Width below Title row */}
          {storageUsage && (
            <div className="mt-6">
              <div className="flex items-center justify-between text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-slate-900">Storage Usage</span>
                  <span>{formatFileSize(storageUsage.totalSize)} / {formatFileSize(storageUsage.quota.totalStorage)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>{storageUsage.totalFiles} library items</span>
                  <span className="px-2 py-0.5 bg-ocean-50 text-ocean-600 rounded-full">{storageUsage.quota.plan} Plan</span>
                </div>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-700 ease-out ${
                    getStoragePercentage() > 90 ? 'bg-rose-500' : 
                    getStoragePercentage() > 75 ? 'bg-amber-500' : 'bg-ocean-500'
                  }`}
                  style={{ width: `${Math.min(getStoragePercentage(), 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-50/50 px-4 border-b border-gray-100">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-6 py-4 text-[13px] font-bold uppercase tracking-widest transition-all duration-300 relative ${
                activeTab === tab.id
                  ? 'text-ocean-600'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <span className="mr-2 text-base opacity-70">{tab.icon}</span>
              {tab.name}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-ocean-500 rounded-t-full shadow-[0_-1px_10px_rgba(14,165,233,0.5)]"></div>
              )}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Upload Progress */}
          {uploadProgress.length > 0 && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-3">Upload Progress</h3>
              <div className="space-y-2">
                {uploadProgress.map((progress) => (
                  <div key={progress.fileId} className="flex items-center space-x-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{progress.fileName}</span>
                        <span className="text-gray-500">{progress.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            progress.status === 'error' ? 'bg-red-500' : 
                            progress.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${progress.progress}%` }}
                        ></div>
                      </div>
                      {progress.error && (
                        <p className="text-xs text-red-600 mt-1">{progress.error}</p>
                      )}
                    </div>
                    <div className="text-sm">
                      {progress.status === 'uploading' && (
                        <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {progress.status === 'completed' && (
                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {progress.status === 'error' && (
                        <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedFiles.length > 0 && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-yellow-800">
                  {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="px-3 py-1 text-sm text-yellow-700 bg-yellow-100 rounded hover:bg-yellow-200 transition-colors"
                  >
                    Clear Selection
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                  >
                    Delete Selected
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="mb-10">
            <div className="relative group">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                accept={activeTab === 'image' ? 'image/*' : activeTab === 'media' ? 'video/*,audio/*' : '*'}
                disabled={isUploading}
              />
              <label 
                htmlFor="file-upload" 
                className={`flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-3xl p-12 bg-slate-50/30 hover:bg-white hover:border-ocean-300 hover:shadow-xl hover:shadow-ocean-500/5 transition-all duration-300 cursor-pointer group/label ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mb-6 group-hover/label:scale-110 group-hover/label:rotate-3 transition-transform duration-300">
                  {isUploading ? (
                    <svg className="animate-spin h-8 w-8 text-ocean-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-8 w-8 text-ocean-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {isUploading ? 'Processing uploads...' : 'Drop your files here'}
                </h3>
                <p className="text-slate-500 text-sm font-medium mb-1">
                  or <span className="text-ocean-600 font-bold underline decoration-2 underline-offset-4">browse your device</span>
                </p>
                {storageUsage && (
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-4 px-3 py-1 bg-slate-100 rounded-full">
                    Limit: {formatFileSize(storageUsage.quota.limits.maxFileSize)} per file
                  </p>
                )}
              </label>
            </div>
          </div>

          {/* Content Grid */}
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-6 border-t border-gray-100 bg-white">
          <div className="text-xs font-black uppercase tracking-widest text-slate-400">
            Current Library View: <span className="text-slate-900">{activeTab} assets</span>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 hover:text-slate-900 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (onSelectFiles && selectedFiles.length > 0) {
                  const chosen = files.filter((file) => file.id && selectedFiles.includes(file.id));
                  onSelectFiles(chosen);
                }
                onClose();
              }}
              className="px-8 py-2.5 bg-ocean-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-ocean-500/25 hover:bg-ocean-700 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Apply Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentManagementModal;
